import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import Seo from "@/components/Seo";
import BookingForm from "@/components/BookingForm";
import CookieConsent from "@/components/CookieConsent";

const REALITY_POINTS = [
    "Leads go cold before they are followed up",
    "Admin work builds up across the team",
    "Tasks are repeated manually",
    "Processes break under pressure",
    "Growth depends on adding more people",
];

const SHOWS_UP = [
    "Sales pipelines slow down",
    "Customer response times increase",
    "Teams rely on workarounds",
    "Reporting does not reflect reality",
    "Leadership lacks visibility across operations",
];

const CALL_PURPOSE = [
    "Understand how your business currently operates",
    "Identify where time and effort are being lost",
    "Highlight where simple systems could improve flow",
    "Determine whether a structured approach is needed",
];

const QUALIFICATION = [
    "You have a team of 10 or more employees",
    "Your business relies on repeatable workflows",
    "Operations or admin are slowing your team down",
    "You want practical improvement, not theory",
];

const NEXT_STEPS = [
    "We review your situation and identify where time is being lost",
    "We outline where simple systems can improve operations",
    "We define the next step based on your business",
];

export default function ConsultationV2() {
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
    }, []);

    return (
        <div className="flex min-h-screen flex-col bg-offwhite text-charcoal" data-testid="page-consultation-v2">
            <Seo
                title="Where is your business losing time? | Chief AI Partners"
                description="A short conversation to identify where time is being lost across daily operations and whether simple systems can fix it."
                path="/consultation-v2"
            />

            {/* Slim header */}
            <header
                className="sticky top-0 z-40 bg-offwhite/95 backdrop-blur-md border-b border-sand"
                data-testid="consultation-v2-header"
            >
                <div className="cap-container flex items-center justify-between py-4">
                    <Link to="/" data-testid="consultation-v2-logo-link" className="flex items-center gap-3.5 md:gap-4">
                        <span className="h-10 w-10 md:h-11 md:w-11 border border-navy flex items-center justify-center font-serif text-navy text-2xl leading-none">
                            C
                        </span>
                        <span className="flex flex-col leading-tight">
                            <span className="font-serif text-navy text-xl md:text-2xl tracking-tight" style={{ fontWeight: 600 }}>
                                Chief AI Partners
                            </span>
                            <span
                                className="text-[11px] md:text-[11.5px] uppercase tracking-[0.28em] text-navy/70 mt-0.5"
                                style={{ fontFamily: "Manrope", fontWeight: 600 }}
                            >
                                Fractional AI Leadership
                            </span>
                        </span>
                    </Link>
                    <a
                        href="#booking"
                        data-testid="consultation-v2-header-cta"
                        className="hidden sm:inline-flex cap-btn-primary text-[0.9rem] px-6 py-3"
                    >
                        Book a 15-minute conversation
                    </a>
                </div>
            </header>

            <main className="flex-1" data-testid="consultation-v2-main">
                {/* HERO */}
                <section className="cap-section pt-20 md:pt-28 pb-12" data-testid="consultation-v2-hero">
                    <div className="cap-container grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
                        <div className="lg:col-span-8">
                            <div className="cap-eyebrow mb-6">15-minute conversation</div>
                            <h1 className="font-serif text-navy text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight max-w-[20ch]">
                                Your business is losing time in more places than you think.
                            </h1>
                        </div>
                        <div className="lg:col-span-4 lg:pb-3 space-y-5">
                            <p className="text-slate text-lg md:text-xl leading-relaxed">
                                Most operational problems are not visible in reports. They show up in delays, missed follow-ups and repeated work.
                            </p>
                            <p className="text-slate text-base leading-relaxed">
                                This conversation helps identify where time is being lost and whether simple systems can fix it.
                            </p>
                            <a
                                href="#booking"
                                data-testid="consultation-v2-hero-cta"
                                className="cap-btn-primary"
                            >
                                Book a 15-minute conversation <ArrowRight size={16} />
                            </a>
                            <ul
                                className="text-sm text-slate space-y-1.5 leading-relaxed max-w-md"
                                data-testid="consultation-v2-hero-reassurance"
                            >
                                <li className="flex items-start gap-2">
                                    <span className="text-teal" aria-hidden="true">&#9679;</span>
                                    <span>No preparation needed</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-teal" aria-hidden="true">&#9679;</span>
                                    <span>No technical knowledge required</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-teal" aria-hidden="true">&#9679;</span>
                                    <span>We will guide the conversation</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                    <div className="cap-container mt-12"><div className="cap-rule" /></div>
                </section>

                {/* REALITY */}
                <section className="cap-section bg-navy text-offwhite" data-testid="consultation-v2-reality">
                    <div className="cap-container grid grid-cols-1 lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-6">
                            <div className="cap-eyebrow mb-4" style={{ color: "#2EA6A1" }}>The Reality</div>
                            <h2 className="font-serif text-offwhite text-4xl md:text-5xl leading-[1.05] tracking-tight">
                                The cost is already inside your business.
                            </h2>
                            <div className="mt-6 space-y-5 text-offwhite/80 text-lg leading-relaxed max-w-xl">
                                <p>Most organisations do not see where time is being lost.</p>
                                <p>It happens across small breakdowns in daily operations that compound over time.</p>
                            </div>
                        </div>
                        <div className="lg:col-span-6">
                            <ul className="divide-y divide-offwhite/15 border-y border-offwhite/15">
                                {REALITY_POINTS.map((p, i) => (
                                    <li
                                        key={p}
                                        className="flex items-start gap-5 py-5"
                                        data-testid={`consultation-v2-reality-${i}`}
                                    >
                                        <span className="cap-numeral text-teal text-2xl min-w-[2.5rem]">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        <span className="font-serif text-offwhite text-2xl leading-snug">{p}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </section>

                {/* REFRAME */}
                <section className="py-28 md:py-36 border-y border-sand" data-testid="consultation-v2-reframe">
                    <div className="cap-container grid grid-cols-1 lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-7">
                            <p className="font-serif text-navy text-4xl md:text-5xl lg:text-6xl leading-[1.04] tracking-tight max-w-[22ch]">
                                This is not a tooling issue.
                            </p>
                        </div>
                        <div className="lg:col-span-5 space-y-5 text-slate text-lg leading-relaxed">
                            <p>Many businesses try new tools. Some improvements happen. Most do not last.</p>
                            <p className="text-navy font-medium">Without structure and ownership, systems do not hold.</p>
                            <p>The result is fragmented workflows and inconsistent outcomes.</p>
                        </div>
                    </div>
                </section>

                {/* TRANSITION: WHERE THIS USUALLY SHOWS UP */}
                <section className="cap-section" data-testid="consultation-v2-shows-up">
                    <div className="cap-container grid grid-cols-1 lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-5">
                            <div className="cap-eyebrow mb-4">Symptoms</div>
                            <h2 className="font-serif text-navy text-4xl md:text-5xl leading-[1.05] tracking-tight">
                                Where this usually shows up.
                            </h2>
                        </div>
                        <div className="lg:col-span-7">
                            <ul className="divide-y divide-sand border-y border-sand">
                                {SHOWS_UP.map((t, i) => (
                                    <li
                                        key={t}
                                        className="flex items-start gap-5 py-5"
                                        data-testid={`consultation-v2-symptom-${i}`}
                                    >
                                        <span className="cap-numeral text-teal text-2xl min-w-[2.5rem]">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        <span className="font-serif text-navy text-2xl leading-snug">{t}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </section>

                {/* WHAT THIS CONVERSATION IS */}
                <section className="cap-section bg-sand/40 border-y border-sand" data-testid="consultation-v2-call-purpose">
                    <div className="cap-container grid grid-cols-1 lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-5">
                            <div className="cap-eyebrow mb-4">The conversation</div>
                            <h2 className="font-serif text-navy text-4xl md:text-5xl leading-[1.05] tracking-tight">
                                What this conversation is for.
                            </h2>
                        </div>
                        <div className="lg:col-span-7">
                            <ul className="space-y-4">
                                {CALL_PURPOSE.map((t, i) => (
                                    <li
                                        key={t}
                                        className="flex items-start gap-4 border-b border-sand pb-4 last:border-b-0"
                                        data-testid={`consultation-v2-purpose-${i}`}
                                    >
                                        <Check className="mt-1.5 text-teal shrink-0" size={18} strokeWidth={2.2} />
                                        <span className="text-navy text-base md:text-lg leading-relaxed">{t}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </section>

                {/* QUALIFICATION */}
                <section className="cap-section" data-testid="consultation-v2-qualification">
                    <div className="cap-container grid grid-cols-1 lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-5">
                            <div className="cap-eyebrow mb-4">Right fit</div>
                            <h2 className="font-serif text-navy text-4xl md:text-5xl leading-[1.05] tracking-tight">
                                This is for you if.
                            </h2>
                        </div>
                        <div className="lg:col-span-7">
                            <ul className="space-y-4">
                                {QUALIFICATION.map((t, i) => (
                                    <li
                                        key={t}
                                        className="flex items-start gap-4 border-b border-sand pb-4 last:border-b-0"
                                        data-testid={`consultation-v2-qualification-${i}`}
                                    >
                                        <Check className="mt-1.5 text-teal shrink-0" size={18} strokeWidth={2.2} />
                                        <span className="text-navy text-base md:text-lg leading-relaxed">{t}</span>
                                    </li>
                                ))}
                            </ul>
                            <p className="text-slate text-sm mt-6 leading-relaxed">
                                This is not suitable for early-stage businesses or those looking for tools only.
                            </p>
                        </div>
                    </div>
                </section>

                {/* TRUST */}
                <section className="cap-section bg-sand/40 border-y border-sand" data-testid="consultation-v2-trust">
                    <div className="cap-container grid grid-cols-1 lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-5">
                            <div className="cap-eyebrow mb-4">Experience</div>
                            <h2 className="font-serif text-navy text-4xl md:text-5xl leading-[1.05] tracking-tight">
                                Built on real operational experience.
                            </h2>
                        </div>
                        <div className="lg:col-span-7 space-y-5 text-slate text-lg leading-relaxed">
                            <p>
                                We have worked inside businesses with direct responsibility for sales performance, operations, team management and system implementation.
                            </p>
                            <p>
                                This experience shapes how we approach operational improvement.
                            </p>
                            <p className="text-navy font-medium">
                                This is not advisory. This is execution.
                            </p>
                            <p>
                                We work with a limited number of organisations at a time to ensure execution quality.
                            </p>
                        </div>
                    </div>
                </section>

                {/* WHAT HAPPENS NEXT */}
                <section className="cap-section" data-testid="consultation-v2-next-steps">
                    <div className="cap-container grid grid-cols-1 lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-5">
                            <div className="cap-eyebrow mb-4">Next</div>
                            <h2 className="font-serif text-navy text-4xl md:text-5xl leading-[1.05] tracking-tight">
                                What happens next.
                            </h2>
                        </div>
                        <div className="lg:col-span-7">
                            <ul className="space-y-4">
                                {NEXT_STEPS.map((t, i) => (
                                    <li
                                        key={t}
                                        className="flex items-start gap-4 border-b border-sand pb-4 last:border-b-0"
                                        data-testid={`consultation-v2-next-step-${i}`}
                                    >
                                        <span className="text-xs uppercase tracking-[0.22em] text-teal font-semibold pt-1.5 min-w-[2.5rem]">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        <span className="text-navy text-base md:text-lg leading-relaxed">{t}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </section>

                {/* PRIMARY CTA */}
                <section className="cap-section bg-navy text-offwhite" data-testid="consultation-v2-primary-cta">
                    <div className="cap-container text-center max-w-3xl mx-auto">
                        <div className="cap-eyebrow mb-4" style={{ color: "#2EA6A1" }}>Next step</div>
                        <h2 className="font-serif text-offwhite text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                            Book a 15-minute conversation.
                        </h2>
                        <p className="mt-6 text-offwhite/80 text-lg leading-relaxed">
                            We will review your situation and confirm if there is a clear path to improvement.
                        </p>
                        <a
                            href="#booking"
                            data-testid="consultation-v2-primary-cta-btn"
                            className="inline-flex items-center justify-center gap-2 bg-offwhite text-navy font-semibold px-6 py-3 mt-8 border border-offwhite hover:bg-sand transition-colors"
                        >
                            Book a 15-minute conversation <ArrowRight size={16} />
                        </a>
                    </div>
                </section>

                {/* FORM */}
                <section
                    id="booking"
                    className="cap-section scroll-mt-24"
                    data-testid="consultation-v2-booking-section"
                >
                    <div className="cap-container max-w-3xl">
                        <div className="cap-eyebrow mb-4">Request a time</div>
                        <h2 className="font-serif text-navy text-4xl md:text-5xl leading-[1.05] tracking-tight">
                            Request a time.
                        </h2>
                        <p className="mt-5 text-slate text-lg leading-relaxed">
                            Share a few details and we will confirm a suitable time for your conversation.
                        </p>
                        <div className="mt-10">
                            <BookingForm
                                eyebrow="Request a time"
                                heading="Tell us about your business"
                                subheading="Share a few details and we will confirm a suitable time for your conversation."
                                successTitle="Thanks. We have received your request."
                                successBody={"We will review your details and confirm a suitable time shortly."}
                                source="consultation_landing_v2"
                                showWebsite={false}
                                submitLabel="Request a time"
                                microReassurance="We review every request to ensure there is a strong fit before confirming a time."
                                onSuccess={() => setSubmitted(true)}
                            />
                        </div>
                    </div>
                </section>

                {/* FINAL REASSURANCE */}
                <section className="pb-32 pt-4" data-testid="consultation-v2-reassurance">
                    <div className="cap-container max-w-3xl text-center space-y-3">
                        <p className="text-navy text-lg md:text-xl font-medium leading-relaxed">
                            We only take on a limited number of clients at a time.
                        </p>
                        <p className="text-slate text-base md:text-lg leading-relaxed">
                            If there is a strong fit, we will outline the next step.
                        </p>
                    </div>
                </section>
            </main>

            {/* Slim footer */}
            <footer className="bg-navy text-offwhite py-10" data-testid="consultation-v2-footer">
                <div className="cap-container flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-[0.85rem] text-offwhite/65">
                    <div>&copy; {new Date().getFullYear()} Sanlux Pty Ltd trading as Chief AI Partners. Sydney, Australia.</div>
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                        <Link to="/privacy" className="hover:text-teal" data-testid="consultation-v2-footer-privacy">Privacy Policy</Link>
                        <Link to="/terms" className="hover:text-teal" data-testid="consultation-v2-footer-terms">Terms of Use</Link>
                        <Link to="/cookie-policy" className="hover:text-teal" data-testid="consultation-v2-footer-cookie">Cookie Policy</Link>
                    </div>
                </div>
            </footer>

            {/* Sticky mobile CTA */}
            {!submitted && (
                <a
                    href="#booking"
                    data-testid="consultation-v2-sticky-cta"
                    className="sm:hidden fixed bottom-4 left-4 right-4 z-30 cap-btn-primary justify-center shadow-sm"
                >
                    Book a 15-minute conversation
                </a>
            )}

            <CookieConsent />
        </div>
    );
}
